// Simple table cell classes - matches original inline styles exactly
export const tableClasses = {
  // Base cell
  cell: 'table-cell',

  // Data cells (type === 'cells')
  data: 'table-data',
  dataBorder: 'table-data-border',

  // Header cells (rowIdx === 0)
  header: 'table-header',
  date: 'table-date',
  auditCount: 'table-audit-count',
  reconcile: 'table-reconcile',

  // Subheader cells (rowIdx === "subheader")
  subheader: 'table-subheader',
  subheaderBorder: 'table-subheader-border',

  // Border modifiers
  noLeftBorder: 'table-no-left-border',
  noRightBorder: 'table-no-right-border',
  noBottomBorder: 'table-no-bottom-border',

  // Text styles
  headerBorderBottom: 'table-header-border-bottom',
  headerText: 'table-header-text',
  subheaderText: 'table-subheader-text',
  verticalText: 'table-vertical-text'
} as const;

// Simple helper functions that match original logic
export const getHeaderClass = () => tableClasses.header;
export const getDateClass = () => `${tableClasses.date} ${tableClasses.noLeftBorder}`;
export const getAuditCountClass = () => tableClasses.auditCount;
export const getReconcileClass = () => tableClasses.reconcile;

export const getDataClass = (hasBorder: boolean = false) =>
  hasBorder ? tableClasses.dataBorder : tableClasses.data;

export const getSubheaderClass = (hasBorder: boolean = false) =>
  hasBorder ? `${tableClasses.subheader} ${tableClasses.subheaderBorder}` : tableClasses.subheader;

// Function to get complete class string with all modifiers
export const getCompleteCellClass = (props: {
  type?: 'cells' | 'no_of_audit';
  rowIdx: number | string;
  colIdx: number;
  totalRows: number;
  totalCols: number;
  borderColumns?: number[];
}) => {
  const { type, rowIdx, colIdx, totalRows, totalCols, borderColumns = [] } = props;
  const classes: string[] = [];

  // Base class based on type and position
  if (type === 'cells') {
    classes.push(getDataClass(borderColumns.includes(colIdx)));
  } else if (rowIdx === 0) {
    if (type === 'no_of_audit') {
      classes.push(tableClasses.auditCount);
    } else if (colIdx === totalCols - 1) { // Reconcile cell (last column)
      classes.push(tableClasses.reconcile);
    } else if (colIdx === 0) { // Date cell
      classes.push(tableClasses.date);
    } else {
      classes.push(tableClasses.header);
    }

    // Add bottom border for super-headers (middle borders)
    if (typeof rowIdx === 'number' && totalRows > 1 && rowIdx < totalRows - 1) {
      classes.push(tableClasses.headerBorderBottom);
    }
  } else if (rowIdx === "subheader") {
    classes.push(getSubheaderClass(borderColumns.includes(colIdx)));
  } else {
    classes.push(tableClasses.cell);
  }

  // Border modifiers
  if (colIdx === 0) classes.push(tableClasses.noLeftBorder);
  if (colIdx === totalCols - 1) classes.push(tableClasses.noRightBorder);
  if (rowIdx === totalRows - 1 || rowIdx === 'subheader') classes.push(tableClasses.noBottomBorder);

  return classes.join(' ');
};

// Simple function for data cells (most common use case)
export const getDataCellClass = (colIdx: number, rowIdx: number = 0, totalRows: number = 2, borderColumns: number[] = [], totalCols?: number) => {
  const computedTotalCols = totalCols ?? (Math.max(colIdx, ...(borderColumns.length ? borderColumns : [0])) + 2);
  return getCompleteCellClass({
    type: 'cells',
    rowIdx,
    colIdx,
    totalRows,
    totalCols: computedTotalCols,
    borderColumns
  });
};

// Function for store table data cells
export const getStoreDataCellClass = (colIdx: number, rowIdx: number = 0, totalRows: number = 2, borderColumns: number[] = [0, 5, 9, 14, 17, 20], totalCols: number = 22) => {
  return getCompleteCellClass({
    type: 'cells',
    rowIdx,
    colIdx,
    totalRows,
    totalCols,
    borderColumns
  });
};

// Debug function to help verify border logic
export const debugCellClasses = (props: {
  type?: 'cells' | 'no_of_audit';
  rowIdx: number | string;
  colIdx: number;
  totalRows: number;
  totalCols: number;
  borderColumns?: number[];
}) => {
  const { type, rowIdx, colIdx, totalRows, totalCols, borderColumns = [] } = props;

  console.log('Cell Debug:', {
    colIdx,
    totalCols,
    isLastColumn: colIdx === totalCols - 1,
    isFirstColumn: colIdx === 0,
    classes: getCompleteCellClass(props)
  });

  return getCompleteCellClass(props);
};
